/**
 * WHICH ASSISTANT FAMILY A ROUTE BELONGS TO.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 * ---------------------------------------------------------------------------
 * The API exposes TWO assistant families and they are not interchangeable:
 *
 *   - the TENANT family (`/ai/assistant/*`) acts on the tenant the caller's
 *     session resolves to, and
 *   - the PLATFORM family (`/ai/platform/assistant/*`) is authorized by the
 *     deployment's platform grants and carries no tenant at all.
 *
 * Calling the wrong one is not a slow failure — it is a refusal, or worse, a
 * successful answer from the wrong authority. So the decision is made ONCE, here,
 * and every caller asks this module. A second `startsWith('/platform')` written
 * somewhere else is a second answer to the same question, and the two answers
 * will eventually disagree.
 *
 * ---------------------------------------------------------------------------
 * THE RULE
 * ---------------------------------------------------------------------------
 * The platform's own pages are the `/platform/*` routes in `src/app/router/routes.tsx`
 * (`/platform/tenants`, `/platform/tenants/new`, `/platform/ai`, …). Everything else
 * is a tenant page — the tenant workspace, the catalogue, the media library, stock,
 * the overview. A platform operator is often a plain member of a tenant, and a
 * merchant owner holds no platform grant at all, so the route is the only honest
 * signal available to the console. The server re-authorizes every request anyway;
 * this decides WHICH question to ask, not whether the answer is permitted.
 */

/** The two assistant families the API exposes. */
export type AssistantScope = 'tenant' | 'platform';

/** The prefix every platform page's route carries. */
const PLATFORM_ROUTE_PREFIX = '/platform';

/**
 * The scope a route belongs to.
 *
 * A route is a PLATFORM route when it is the platform prefix itself or sits
 * under it. The comparison is on whole path SEGMENTS rather than a raw prefix
 * match, so a hypothetical `/platform-operator` page is not mistaken for a
 * platform page — the segment boundary is what makes the rule safe to extend.
 */
export function assistantScopeForRoute(pathname: string): AssistantScope {
  if (pathname === PLATFORM_ROUTE_PREFIX) return 'platform';
  if (pathname.startsWith(`${PLATFORM_ROUTE_PREFIX}/`)) return 'platform';
  return 'tenant';
}
